import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import {
  LayoutDashboard, Bell, Settings, Users, LogOut, Menu, X,
  ChevronDown, Wrench,
} from 'lucide-react';
import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import safeproLogo from '../../assets/safepro-logo.svg';

export default function Navbar() {
  const { profile, signOut } = useAuth();
  const location = useLocation();
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [asetuksetOpen, setAsetuksetOpen] = useState(false);
  const [mobileAsetuksetOpen, setMobileAsetuksetOpen] = useState(false);
  const asetuksetRef = useRef(null);

  const role = profile?.role;
  const isAdmin = role === 'admin' || role === 'superadmin';
  const showAsetukset = !!role; // Huolto (#86): vain kielivalinta Käyttäjät-sivulla

  const navItems = [
    { to: '/dashboard', label: t('nav.dashboard'), icon: LayoutDashboard },
    { to: '/alerts',    label: t('nav.alerts'),    icon: Bell },
    // Huoltokirjaukset (#82): Superadmin ja Huolto-rooli (#86)
    ...(role === 'superadmin' || role === 'huolto' ? [{ to: '/huolto', label: t('nav.maintenance'), icon: Wrench }] : []),
  ];

  const asetuksetItems = [
    { to: '/users',    label: t('nav.users'),        icon: Users,    adminOnly: false },
    { to: '/settings', label: t('nav.alertSettings'), icon: Settings, adminOnly: true  },
  ].filter(item => !item.adminOnly || isAdmin);

  const items = [...navItems];

  function isActive(path) {
    return location.pathname.startsWith(path);
  }

  const asetuksetActive = location.pathname.startsWith('/users') || location.pathname.startsWith('/settings');

  useEffect(() => {
    function handleClickOutside(e) {
      if (asetuksetRef.current && !asetuksetRef.current.contains(e.target)) {
        setAsetuksetOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <nav className="bg-brand-900 text-white">
      <div className="max-w-7xl mx-auto px-4 flex items-center justify-between h-14">
        <Link to="/dashboard" className="flex items-center gap-2">
          <img src={safeproLogo} alt="Safe Digi" className="h-8 brightness-0 invert" />
          {profile?.tenant_name && (
            <span className="text-white font-semibold text-sm hidden sm:block">{profile.tenant_name}</span>
          )}
        </Link>

        <div className="hidden md:flex items-center gap-1">
          {items.map(({ to, label, icon: Icon }) => (
            <Link
              key={to}
              to={to}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded text-sm transition-colors
                ${isActive(to) ? 'bg-white/20' : 'hover:bg-white/10'}`}
            >
              <Icon size={15} />
              {label}
            </Link>
          ))}

          {/* Asetukset-dropdown */}
          {showAsetukset && (
            <div className="relative" ref={asetuksetRef}>
              <div className={`flex items-center rounded text-sm transition-colors ${asetuksetActive ? 'bg-white/20' : 'hover:bg-white/10'}`}>
                <button
                  onClick={() => setAsetuksetOpen(v => !v)}
                  className="flex items-center gap-1.5 pl-3 pr-2 py-1.5"
                >
                  <Settings size={15} />
                  {t('nav.settings')}
                </button>
                <button
                  onClick={() => setAsetuksetOpen(v => !v)}
                  className="pr-2 py-1.5 hover:text-white/70"
                  aria-label={t('nav.settings')}
                >
                  <ChevronDown size={13} className={`transition-transform ${asetuksetOpen ? 'rotate-180' : ''}`} />
                </button>
              </div>
              {asetuksetOpen && (
                <div className="absolute top-full left-0 mt-1 bg-white rounded-xl shadow-lg border border-gray-100 py-1 min-w-[180px] z-50">
                  {asetuksetItems.map(({ to, label, icon: Icon }) => (
                    <Link
                      key={to}
                      to={to}
                      onClick={() => setAsetuksetOpen(false)}
                      className={`flex items-center gap-2.5 px-4 py-2 text-sm transition-colors
                        ${isActive(to) ? 'bg-brand-50 text-brand-700 font-medium' : 'text-gray-700 hover:bg-gray-50'}`}
                    >
                      <Icon size={14} className={isActive(to) ? 'text-brand-600' : 'text-gray-400'} />
                      {label}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="ml-3 pl-3 border-l border-white/20 flex items-center gap-3">
            <span className="text-xs text-white/50 hidden lg:block">{profile?.email}</span>
            <button
              onClick={signOut}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded text-sm hover:bg-white/10"
            >
              <LogOut size={15} />
              {t('nav.logout')}
            </button>
          </div>
        </div>

        <button className="md:hidden" onClick={() => setOpen(!open)}>
          {open ? <X size={20} /> : <Menu size={20} />}
        </button>
      </div>

      {open && (
        <div className="md:hidden px-4 pb-3 flex flex-col gap-1">
          {items.map(({ to, label, icon: Icon }) => (
            <Link
              key={to}
              to={to}
              onClick={() => setOpen(false)}
              className={`flex items-center gap-2 px-3 py-2 rounded text-sm
                ${isActive(to) ? 'bg-white/20' : 'hover:bg-white/10'}`}
            >
              <Icon size={15} />
              {label}
            </Link>
          ))}

          {/* Asetukset-osio mobiilissa */}
          {showAsetukset && (
            <>
              <button
                onClick={() => setMobileAsetuksetOpen(v => !v)}
                className={`flex items-center gap-2 px-3 py-2 rounded text-sm w-full text-left
                  ${asetuksetActive ? 'bg-white/20' : 'hover:bg-white/10'}`}
              >
                <Settings size={15} />
                {t('nav.settings')}
                <ChevronDown size={13} className={`ml-auto transition-transform ${mobileAsetuksetOpen ? 'rotate-180' : ''}`} />
              </button>
              {mobileAsetuksetOpen && (
                <div className="pl-4 flex flex-col gap-0.5">
                  {asetuksetItems.map(({ to, label, icon: Icon }) => (
                    <Link
                      key={to}
                      to={to}
                      onClick={() => { setOpen(false); setMobileAsetuksetOpen(false); }}
                      className={`flex items-center gap-2 px-3 py-2 rounded text-sm
                        ${isActive(to) ? 'bg-white/20' : 'hover:bg-white/10'}`}
                    >
                      <Icon size={14} />
                      {label}
                    </Link>
                  ))}
                </div>
              )}
            </>
          )}

          <button
            onClick={signOut}
            className="flex items-center gap-2 px-3 py-2 rounded text-sm hover:bg-white/10"
          >
            <LogOut size={15} />
            {t('nav.logout')}
          </button>
        </div>
      )}
    </nav>
  );
}
